import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Controlled bottom sheet (mobile modal that slides up from the bottom edge).
 *
 * Rendered into a portal at `document.body` — same as `Popover` — so it never
 * sits inside a transformed ancestor, which would break `position: fixed` on
 * iOS. Sheets can stack: each instance saves and restores the previous
 * `document.body.style.overflow`, so nested open/close composes correctly.
 *
 * Intentionally no autofocus: focusing an input on open would summon the iOS
 * keyboard and cover most of the sheet.
 */
interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  ariaLabel: string;
  children: ReactNode;
  /** Stacking order; a sub-sheet layered over a base sheet passes 60. */
  zIndex?: number;
  /**
   * The base sheet sets this to false while a sub-sheet is open, so a single
   * Escape only closes the top-most sheet.
   */
  closeOnEscape?: boolean;
}

export function BottomSheet({
  open,
  onClose,
  ariaLabel,
  children,
  zIndex = 50,
  closeOnEscape = true,
}: BottomSheetProps) {
  // Body scroll lock while open (save/restore pattern so stacked sheets compose).
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !closeOnEscape) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, closeOnEscape, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="ui-sheet-backdrop" style={{ zIndex }} onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className="ui-sheet-panel"
        style={{ zIndex: zIndex + 1 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ui-sheet-handle" aria-hidden />
        {children}
      </div>
    </div>,
    document.body,
  );
}
